import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../../contexts/AuthContext';
import { loadLocalTeam } from '../localTeam';

export interface ActiveTeam {
  id: string;
  name: string | null;
  age_group: string | null;
}

/**
 * Resolves the coach's active team — the team THIS coach owns.
 *
 * Scoped to `coach_id = user.id`. We must NOT do an unfiltered `limit(1)`
 * across teams and lean on RLS alone: on a shared DB (demo + QA orgs), a
 * coach's RLS lets them read other orgs' teams, so an unfiltered pick can
 * resolve to someone else's squad (e.g. the demo "Monty FC"), which then
 * surfaces that team's sessions/drills. Filtering by coach_id keeps a review
 * coach (Ben) pinned to his own seeded squad.
 *
 * Falls back to the localStorage team the Onboarding flow saves when the DB
 * is unreachable.
 */
export function useActiveTeam() {
  const { user } = useAuth();
  const [team, setTeam] = useState<ActiveTeam | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (user?.id) {
          const { data } = await supabase
            .from('teams')
            .select('id, name, age_group')
            .eq('coach_id', user.id)
            .is('deleted_at', null)
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle();
          if (cancelled) return;
          if (data) {
            setTeam(data as ActiveTeam);
            setLoading(false);
            return;
          }
        }
      } catch {
        // fall through to local fallback
      }
      if (cancelled) return;
      const local = loadLocalTeam();
      setTeam(local ? { id: local.id, name: local.name, age_group: local.age_group } : null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  return { team, loading };
}
